import React, { FC } from 'react';
import clsx from 'clsx';
import { Icon, Image, Text, Types } from '@marriott/mi-ui-library';
import { MLogoContainer, MLogoContainerBackgroundColor } from '@marriott/mi-web-ui-kit';

import { formatDateRange } from '../../modules/utils/helper';

export type ReservationCardProps = {
  propertyImageSrc?: string;
  propertyImageAltText?: string;
  propertyName?: string;
  brandId?: string;
  checkInDate?: string;
  checkOutDate?: string;
  confirmationNumber?: string;
  confirmationNumberLabel?: string;
  footerLabel?: string;
  footerIconClass?: string;
  /** Renders the footer chrome even when label/icon are absent (Figma reservation card footer row). */
  showFooterRow?: boolean;
  locale: string;
  testId?: string;
};

export const ReservationCard: FC<ReservationCardProps> = ({
  propertyImageSrc,
  propertyImageAltText = '',
  propertyName,
  brandId,
  checkInDate,
  checkOutDate,
  confirmationNumber,
  confirmationNumberLabel,
  footerLabel,
  footerIconClass,
  showFooterRow = false,
  locale,
  testId = 'reservation-card',
}) => {
  const stayDates =
    checkInDate && checkOutDate ? formatDateRange(checkInDate, checkOutDate, locale)?.toUpperCase() : undefined;
  const hasFooter = showFooterRow || Boolean(footerLabel || footerIconClass);

  return (
    <div className="wallet-award-modal__reservation-card" data-testid={testId}>
      <div className="wallet-award-modal__reservation-main">
        <div className="wallet-award-modal__reservation-image" aria-hidden="true">
          {propertyImageSrc ? (
            <Image
              defaultImageURL={propertyImageSrc}
              altText={propertyImageAltText}
              customClass="wallet-award-modal__reservation-image-el"
            />
          ) : (
            <div
              className="wallet-award-modal__reservation-image-placeholder d-flex align-items-center justify-content-center"
              aria-hidden="true"
            >
              <Icon iconClass="wallet-award-modal__reservation-image-placeholder-icon icon-hotel icon-xl" />
            </div>
          )}
        </div>

        <div className="wallet-award-modal__reservation-details">
          {stayDates ? (
            <Text
              element={Types.tags.div}
              fontSize={Types.size.extraSmall}
              copyText={stayDates}
              customClass="wallet-award-modal__reservation-dates t-font-xs t-font-weight-medium"
            />
          ) : null}
          {propertyName ? (
            <span className="wallet-award-modal__reservation-name t-subtitle-m">{propertyName}</span>
          ) : null}
          {confirmationNumber ? (
            <Text
              element={Types.tags.div}
              fontSize={Types.size.small}
              copyText={
                confirmationNumberLabel ? `${confirmationNumberLabel}: ${confirmationNumber}` : confirmationNumber
              }
              customClass="wallet-award-modal__reservation-confirmation"
            />
          ) : null}
        </div>

        <div
          className="wallet-award-modal__reservation-brand d-flex flex-shrink-0 align-items-center justify-content-center"
          data-testid={`${testId}-brand`}
        >
          {brandId ? (
            <MLogoContainer
              brand={brandId}
              aspectRatio="3:2"
              width={84}
              height={56}
              backgroundColor={MLogoContainerBackgroundColor.Inverse}
            />
          ) : (
            <div className="wallet-award-modal__reservation-brand-placeholder" aria-hidden="true" />
          )}
        </div>
      </div>

      {hasFooter ? (
        <div className="wallet-award-modal__reservation-footer" data-testid={`${testId}-footer`}>
          {footerIconClass ? (
            <Icon
              iconClass={clsx('wallet-award-modal__reservation-footer-icon', 'icon-decorative', footerIconClass)}
              isDecorative
              ariaLabel=""
            />
          ) : null}
          {footerLabel ? <span className="wallet-award-modal__reservation-footer-label">{footerLabel}</span> : null}
        </div>
      ) : null}
    </div>
  );
};
